import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { logError } from '../utils/logError';

const AuthContext = createContext(null);

const TOKEN_KEY = 'access_token';
// JWT expiry'yi her 30 saniyede bir kontrol et
const TOKEN_CHECK_INTERVAL_MS = 30 * 1000;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionExpiredMsg, setSessionExpiredMsg] = useState(null);
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const expiryCheckRef = useRef(null);

    useEffect(() => {
        // Check for existing token on load
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
            try {
                const payload = parseJwt(token);
                // Simple expiry check
                if (payload && payload.exp * 1000 > Date.now()) {
                    setUser({
                        id: payload.user_id,
                        email: payload.sub,
                        role: payload.role,
                        lokasyon_id: payload.lokasyon_id ?? null,
                        customPermissions: payload.custom_permissions || [],
                        effectivePermissions: null,
                        name: payload.sub.split('@')[0],
                        department: mapRoleToDepartment(payload.role)
                    });
                    setMustChangePassword(!!payload.must_change);
                    // Backend'den güncel custom permissions'ı al (JWT stale olabilir)
                    refreshUserFromServer(token);
                } else {
                    // Token zaten süresi dolmuş — temizle
                    localStorage.removeItem(TOKEN_KEY);
                }
            } catch (e) {
                localStorage.removeItem(TOKEN_KEY);
            }
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ──── JWT Expiry Monitor ────────────────────────────────────────
    // Token süresi dolunca otomatik logout + kullanıcıya bildir.
    // Eskiden bu olmadığı için 24 saat sonra kullanıcılar "sessizce atılıyor"du.
    useEffect(() => {
        if (!user) {
            if (expiryCheckRef.current) {
                clearInterval(expiryCheckRef.current);
                expiryCheckRef.current = null;
            }
            return;
        }

        const check = () => {
            const token = localStorage.getItem(TOKEN_KEY);
            if (!token) return;
            const payload = parseJwt(token);
            if (!payload || !payload.exp) return;
            const expMs = payload.exp * 1000;
            const remaining = expMs - Date.now();
            if (remaining <= 0) {
                // Süre doldu — logout + bildir
                handleSessionExpired();
            }
            // (İstenirse remaining < 5 dk kala uyarı toast'u eklenebilir)
        };

        // İlk hemen, sonra interval
        check();
        expiryCheckRef.current = setInterval(check, TOKEN_CHECK_INTERVAL_MS);

        return () => {
            if (expiryCheckRef.current) clearInterval(expiryCheckRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // sessionExpired msg gösterildikten sonra 7 saniye sonra temizle
    useEffect(() => {
        if (!sessionExpiredMsg) return;
        const t = setTimeout(() => setSessionExpiredMsg(null), 7000);
        return () => clearTimeout(t);
    }, [sessionExpiredMsg]);

    const handleSessionExpired = () => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setSessionExpiredMsg('Oturumunuz sona erdi. Lütfen tekrar giriş yapın.');
        // login sayfasında değilsek yönlendir
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    };

    const refreshUserFromServer = async (token) => {
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
            const res = await fetch(`${API_URL}/api/auth/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.status === 401) {
                // Backend de süresi dolmuş diyor — logout
                handleSessionExpired();
                return;
            }
            if (!res.ok) return;
            const data = await res.json();
            setUser(prev => prev ? ({
                ...prev,
                role: data.role,
                lokasyon_id: data.lokasyon_id ?? null,
                customPermissions: data.custom_permissions || [],
                // PR2: DB-driven effective izinler (rol DB ∪ custom). hasPermission birincil kaynağı.
                effectivePermissions: data.effective_permissions || [],
            }) : prev);
            setMustChangePassword(!!data.sifre_degistirilmeli);
        } catch (e) {
            // sessizce geç — JWT'deki info fallback olarak kalır
        }
    };

    const login = async (email, password) => {
        try {
            const formData = new FormData();
            formData.append('username', email);
            formData.append('password', password);

            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Giriş başarısız');
            }

            const data = await response.json();
            localStorage.setItem(TOKEN_KEY, data.access_token);

            const payload = parseJwt(data.access_token);
            setUser({
                id: payload.user_id,
                email: payload.sub,
                role: payload.role,
                lokasyon_id: payload.lokasyon_id ?? null,
                customPermissions: payload.custom_permissions || [],
                effectivePermissions: null,
                name: payload.sub.split('@')[0],
                department: mapRoleToDepartment(payload.role)
            });
            setMustChangePassword(!!payload.must_change);
            // PR2: login sonrası /me ile effective_permissions yükle (DB-driven hasPermission)
            refreshUserFromServer(data.access_token);
            // Login başarılı → eski "session expired" mesajını temizle
            setSessionExpiredMsg(null);
            return true;
        } catch (error) {
            logError(error);
            return false;
        }
    };

    const changePassword = async (currentPassword, newPassword) => {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
        const token = localStorage.getItem(TOKEN_KEY);
        const res = await fetch(`${API_URL}/api/auth/change-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { ok: false, error: err.detail || 'Şifre değiştirilemedi' };
        }
        const data = await res.json();
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setMustChangePassword(false);
        return { ok: true };
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        setMustChangePassword(false);
    };

    const parseJwt = (token) => {
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            return null;
        }
    };

    const mapRoleToDepartment = (role) => {
        const map = {
            'admin': 'Sistem Yönetimi',
            'yonetim': 'Yönetim',
            'yonetici_ai':'Yönetim AI',
            'satinalma': 'Satınalma',
            'uretim': 'Üretim',
            'depo': 'Depo',
            'muhasebe': 'Muhasebe',
            'operasyon': 'Operasyon',
            'sube': 'Şube',
            'ik': 'İnsan Kaynakları',
        };
        return map[role] || 'Genel';
    };

    // sessionExpired banner — global olarak login sayfası dahil her yerde görünür
    const banner = sessionExpiredMsg ? (
        <div role="alert" style={{
            position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, background: '#dc3545', color: 'white',
            padding: '12px 24px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            fontSize: 14, fontWeight: 500,
        }}>
            🔒 {sessionExpiredMsg}
        </div>
    ) : null;

    return (
        <AuthContext.Provider value={{ user, login, logout, loading, sessionExpired: !!sessionExpiredMsg, mustChangePassword, changePassword }}>
            {banner}
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
